import MakulaLogo from "@shared/svg/makula-logo-dark.svg?react";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button from "@shared/ui/Button";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import Toast from "@shared/ui/Toast";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import AuthPageLayout from "~/components/AuthPageLayout";
import ContentLoading from "~/components/ContentLoading";
import { SignUpForm } from "~/features/SignUpForm";
import { useResetPassword, useVerifyPasswordResetToken } from "~/services/auth";
import { JWT } from "~/apollo/_config";

function useQuery() {
  return new URLSearchParams(useLocation().search.toString());
}

const ResetPassword = () => {
  const authToken = localStorage.getItem(JWT.LOCAL_STORAGE.TOKEN.NAME);
  const navigate = useNavigate();
  const query = useQuery();
  const uid = query.get("uid");
  const token = query.get("token");
  const { mutation, loading } = useResetPassword();
  const { verify, loading: verifying } = useVerifyPasswordResetToken();
  const { messages: msg } = useIntl();

  const [email, setEmail] = useState(null);
  const [tokenInvalid, setTokenInvalid] = useState(false);

  useEffect(() => {
    if (authToken) return navigate("/app");

    if (!uid || !token) return;
    verify({ uid, token })
      .then((resolvedEmail) => {
        if (resolvedEmail) setEmail(resolvedEmail);
        else setTokenInvalid(true);
      })
      .catch(() => setTokenInvalid(true));
  }, []);

  const handleResetSubmit = async (password) => {
    await mutation({ uid, token, password });
    toast(<Toast message={msg?.login?.resetPassword.successToast} />, {
      closeButton: false,
    });
    navigate("/app", { replace: true });
  };

  if (!uid || !token || tokenInvalid) {
    return (
      <AuthPageLayout noBanner>
        <div className="flex flex-col gap-lg">
          <MakulaLogo className="h-[48px] w-fit" />
          <div className="flex flex-col gap-2 mb-4">
            <Headline size={HEADLINE_SIZES.MEDIUM} className="mb-xs">
              {msg?.login?.resetPassword.invalidLink}
            </Headline>
            <BodyText size={BODY_TEXT_SIZES.SMALL} color="text-secondary">
              {msg?.login?.resetPassword.invalidLinkMessage}
            </BodyText>
          </div>
          <Button
            fullWidth={true}
            text={msg?.login?.resetPassword.backToLogin}
            onClick={() => navigate("/", { replace: true })}
          />
        </div>
      </AuthPageLayout>
    );
  }

  if (verifying) {
    return (
      <AuthPageLayout noBanner>
        <div className="flex flex-col gap-lg">
          <MakulaLogo className="h-[48px] w-fit" />
          <ContentLoading />
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout noBanner>
      <SignUpForm
        messageNamespace="resetPassword"
        email={email}
        isLoading={loading}
        onSubmit={handleResetSubmit}
      />
    </AuthPageLayout>
  );
};

export default ResetPassword;
